import Tippy from '@tippyjs/react';
import classNames from 'classnames';
import { forwardRef, useImperativeHandle, useState } from 'react';

import 'tippy.js/themes/light.css';
import 'tippy.js/dist/tippy.css';

import {
  ITippyTooltipProps,
  TippyTooltipRef,
} from '@/shared/components/atoms/TippyTooltip/ITippyTooltip';

const TippyTooltip = forwardRef<TippyTooltipRef, ITippyTooltipProps>(
  (
    {
      padding,
      content,
      children,
      maxWidth,
      trigger,
      theme,
      placement,
      hideOnContentClick = true,
      ...props
    },
    ref
  ) => {
    const isClickTrigger = trigger === 'click';

    const [visible, setVisible] = useState(false);

    const hide = () => setVisible(false);
    const toggle = () => setVisible((state) => !state);

    // Expose hide and toggle to the parent via ref
    useImperativeHandle(ref, () => ({
      hide,
      toggle,
    }));

    return (
      <Tippy
        appendTo={document.body}
        interactive
        placement={placement ?? 'auto'}
        // make tooltip controlled only if the trigger is click, so that content can be hidden on click
        visible={isClickTrigger ? visible : undefined}
        trigger={isClickTrigger ? undefined : trigger}
        onClickOutside={hide}
        theme={theme ?? 'light'}
        maxWidth={maxWidth ?? 'none'}
        content={
          <div
            onClick={hideOnContentClick ? hide : undefined}
            className={classNames(
              padding ?? 'px-16 py-8',
              'max-h-[100dvh] scrollbar-thin'
            )}
          >
            {content}
          </div>
        }
        {...props}
      >
        <div
          onClick={toggle}
          className={classNames(
            isClickTrigger && !props?.disabled && 'cursor-pointer'
          )}
        >
          {children}
        </div>
      </Tippy>
    );
  }
);

export default TippyTooltip;
